import * as R from 'ramda';
import moment from 'moment';
import message from 'antd/lib/message';
import { compareAsc } from 'date-fns/fp';

import { THOUSAND_SEPARATOR } from './regexp';

/**
 * listToItems :: [MongoDocument] -> Object
 * MongoDocument = Object
 */
export const listToItems = (array, key = '_id') =>
  R.reduce((acc, x) => R.assoc(x[key], x, acc), {})(array);

/**
 * round :: Number -> Number
 */
export const round = num => Math.round(num * 100) / 100;

export const setDocumentTitle = (title) => {
  document.title = title;
};

export const debounce = (inner, ms = 0) => {
  let timer = null;
  let resolves = [];

  return (...args) => {
    // Run the function after a certain amount of time
    clearTimeout(timer);
    timer = setTimeout(() => {
      // Get the result of the inner function, then apply it to the resolve function of
      // each promise that has been created since the last time the inner function was run
      const result = inner(...args);
      resolves.forEach(r => r(result));
      resolves = [];
    }, ms);

    return new Promise(r => resolves.push(r));
  };
};

export const getFullUrl = (urlPath) => {
  const path = urlPath.startsWith('/') ? urlPath : `/${urlPath}`;
  return `${window.location.origin}${path}`;
};

export const getItemByPath = (path, list) => R.pathOr({}, path, list);
export const getItemById = (id, list = []) => R.pathOr({}, ['items', id], list);
export const getItemsById = (ids = [], list) => R.values(R.pick(ids, R.propOr({}, 'items', list)));

/**
 * disabledPastDays :: Object | String -> Boolean
 */
export const disabledPastDays = currentDate => currentDate < moment().startOf('day');

/**
 * disabledFutureDays :: Object | String -> Boolean
 */
export const disabledFutureDays = currentDate => currentDate > moment().endOf('day');

/**
 * disabledFutureHours :: Object | String -> Boolean
 */
export const disabledFutureHours = () => {
  const currentHour = moment().hour();

  return [...Array(24).keys()]
    .filter(h => h > currentHour);
};

/**
 * disabledFutureMinutes :: Object | String -> Boolean
 */
export const disabledFutureMinutes = () => {
  const currentMinute = moment().minute();

  return [...Array(60).keys()]
    .filter(h => h > currentMinute);
};

/**
 * copy to clipboard
 */
export const copyToClipboard = (text, e) => {
  e.stopPropagation();
  navigator.clipboard.writeText(text);
  message.success('Copied to clipboard!');
};

// thousand separator
export const thousandSeparator = number => number && number.toString().replace(THOUSAND_SEPARATOR, ' ');

/**
 * areArraysEqual :: (Array, Array) -> Boolean
 */
export const areArraysEqual = (firstArr = [], secondArr = []) => R.compose(
  res => res.length === firstArr.length && res.length === secondArr.length,
  R.intersection,
)(firstArr, secondArr);

/**
 * firstContainsSecond :: (Array, Array) -> Boolean
 * check if even one element from the first array presents in the second array
 */
export const firstContainsSecond = (firstArr = [], secondArr = []) =>
  firstArr.some(i => secondArr.includes(i));

/**
 * roundToThousand :: Number -> Number
 */
export const roundToThousand = num => Number((num / 1000).toFixed(1));

/**
 * stripHtml :: String -> String
 * returns the text from a HTML string
 */
export const stripHtml = (html) => {
  // Create a new div element
  const temporalDivElement = document.createElement('div');
  // Set the HTML content with the provided
  temporalDivElement.innerHTML = html || '';
  // Retrieve the text property of the element (cross-browser support)
  return temporalDivElement.textContent || temporalDivElement.innerText || '';
};

/**
 * sortDataByCreatedAt :: [Object] -> [Object]
 */
export const sortDataByCreatedAt = R.sortWith([
  (a, b) => compareAsc(a.createdAt, b.createdAt),
]);

/**
 * sortDataByAlphabet :: [Object] -> [Object]
 * field - by what field we need to sort data
 */
export const sortDataByAlphabet = (field, data) => R.sortWith([
  (a, b) => a[field].localeCompare(b[field]),
])(data);

/**
 * truncate :: [String, Number] -> [String]
 */
export const truncate = (str = '', maxSymbols = 60) => {
  if (str.length > maxSymbols) {
    return `${str.substring(0, maxSymbols)}...`;
  }
  return str;
};

/**
 * capitalize :: String -> String
 */
export const capitalize = str => str && str.charAt(0).toUpperCase() + str.slice(1);



// WEBPACK FOOTER //
// ./src/utils/general.js